import {AxiosError} from "axios";
import {useState} from "react";
import Button from 'react-bootstrap/Button';
import Form from 'react-bootstrap/Form';
import Modal from 'react-bootstrap/Modal';
import {useTranslation} from "react-i18next";
import {useNavigate} from "react-router-dom";
import {ApiConfig} from "../api/admin/ApiConfig";
import {CRLApi, ObjectEncoding, X509CRLDetails} from "../api/admin/generated";
import {Download} from "../common/Download";
import {DialogCloseCause} from "../common/Enums";
import {RenderAlert} from "../common/RenderAlert";
import {logger} from "../utils/Logger";
import {Utils} from "../utils/Utils";

/**
 * The OnClose event should be handled by the owner. The owner is responsible for closing the dialog.
 */
type OnClose = (closeCause : DialogCloseCause) => void;

interface CRLExportDialogProps {
    crls : X509CRLDetails[]
    onClose : OnClose
}

/*
 * REST api for accessing the back-end
 */
const crlApi = new CRLApi(ApiConfig.config());

/**
 * Represents a dialog for exporting CRLs.
 *
 * @param {Object} crls - The list of CRLs to export.
 * @param {function} onClose - The callback function to be called when the dialog is closed.
 *
 * @returns {ReactElement} - The CRLExportDialog component.
 */
export const CRLExportDialog = ({crls, onClose} : CRLExportDialogProps ) =>
{
    /**
     * Navigate is used to navigate to the login page if the user is no longer authenticated
     */
    const navigate = useNavigate();
    /**
     * The encoding (PEM vs DER) of the export
     */
    const [encoding, setEncoding] = useState<ObjectEncoding>(ObjectEncoding.Pem);
    /**
     * If true, a Download component will be loaded which will download the binary property
     */
    const [download, setDownload] = useState(false);
    /**
     * The blob containing the binary file to download
     */
    const [blob, setBlob] = useState<Blob>();
    /**
     * The error message if some general error occurred
     */
    const [errorMessage, setErrorMessage] = useState<string>();

    /**
     * i18next translation hook
     */
    const {t} = useTranslation();

    /**
     * The filename for the CRL file depends on the encoding and on the number of exported CRLs
     */
    function getFileExtension()
    {
        if (encoding === ObjectEncoding.Pem) {
            return "pem";
        }

        return crls.length > 1 ? "p7b" : "crl";
    }

    /**
     * The content-type for the CRL file depends on the encoding and on the number of exported CRLs
     */
    function getContentType()
    {
        if (encoding === ObjectEncoding.Pem) {
            return "application/x-pem-file ";
        }

        return crls.length > 1 ? "application/x-pkcs7-certificates" : "application/pkix-crl";
    }

    const exportCRLs = () =>
    {
        // NOTE: responseType must be set to 'blob' because the response is binary
        crlApi.cRLControllerExportCRLs(encoding, crls.map(crl => crl.thumbprint),
                    {responseType : 'blob'}).then((response) =>
        {
            setBlob(new Blob([response.data], {type: getContentType()}));

            // enable the Download component
            setDownload(true);
        })
        .catch((err: Error | AxiosError) => {
            logger.error("Error exporting CRLs", err);
            Utils.redirectToLoginIfNotAuthenticated(err, navigate);
            setErrorMessage(Utils.getErrorMessage(err, t));
        })
    }

    /**
     * Is called when the download started. This should reset download to make sure the Download component is no
     * longer added.
     */
    const onDownload = () =>
    {
        // disable the Download component
        setDownload(false);
        onClose(DialogCloseCause.Succes);
    }

    // called when the dialog is closed without clicking export
    const onHideDialog = () => {
        onClose(DialogCloseCause.Cancel);
    }

    return (
        <Modal show={true} onHide={() => onHideDialog()} backdrop="static">
            <Modal.Header closeButton>
                <Modal.Title>{t("crls.export-selected-crls")}</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                <RenderAlert message={errorMessage} setMessage={setErrorMessage} tooltipPlacement={"bottom"}/>
                <Form>
                    <Form.Group className="mb-3">
                        <Form.Label>{t("common.encoding")}</Form.Label>
                        <Form.Check value="PEM"
                                    type="radio"
                                    name="encoding"
                                    checked={encoding === ObjectEncoding.Pem}
                                    onChange={(e) => setEncoding(Utils.stringToEnum(ObjectEncoding, e.target.value as ObjectEncoding))}
                                    label="PEM"

                        />
                        <Form.Check value="DER"
                                    type="radio"
                                    name="encoding"
                                    checked={encoding === ObjectEncoding.Der}
                                    onChange={(e) => setEncoding(Utils.stringToEnum(ObjectEncoding, e.target.value as ObjectEncoding))}
                                    label="DER"
                        />
                    </Form.Group>
                </Form>
            </Modal.Body>
            <Modal.Footer>
                <Button variant="success"
                        onClick={exportCRLs}>
                    {t("common.export")}
                </Button>
            </Modal.Footer>

            {download && blob !== undefined && (
                <Download blob={blob}
                          filename={`crls.${getFileExtension()}`}
                          onDownload={onDownload} />
            )}
        </Modal>
    )
}
